import React, { useMemo } from 'react';
import styles from './AutoBookCover.module.css';

// Rendered "bound book" cover for items without cover art: cloth-textured
// gradient, a spine with a lit hinge groove, and the title set in serif on
// the cover face. Used by the library grid, player, habit hero, and the
// uploader preview so a coverless book looks like the same object everywhere.

// Warm, on-brand gradient pairs for auto-generated covers.
const AUTO_COVER_GRADIENTS = [
  ['#C8623B', '#7E3620'], // terracotta
  ['#C98A2B', '#6F4612'], // amber/gold
  ['#5E7A52', '#33492C'], // sage
  ['#B0563F', '#5E2317'], // clay rose
  ['#3E6B66', '#1F3D39'], // dusty teal
  ['#9A6A2E', '#4F3413'], // bronze
  ['#7C5A8F', '#3C2A47'], // muted plum
  ['#3A2C25', '#181210'], // espresso
];

const hashString = (value) => {
  const str = String(value || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash;
};

// Storage convention: books are stored as "Title - Author" in their name
// field (no backend schema for author). These two helpers are the single
// boundary where that convention is split apart and put back together.
export const splitTitleAndAuthor = (name) => {
  const parts = String(name || '').split(' - ');
  return {
    title: parts[0] || 'Untitled',
    author: parts.slice(1).join(' - ') || ''
  };
};

export const composeTitleAndAuthor = (title, author) => {
  const cleanTitle = String(title || '').trim() || 'Untitled';
  const cleanAuthor = String(author || '').trim();
  return cleanAuthor ? `${cleanTitle} - ${cleanAuthor}` : cleanTitle;
};

/**
 * @param {string} title    Book title, printed on the cover.
 * @param {string} author   Optional author, small caps under the title.
 * @param {string} typeLabel Optional badge (e.g. "Document") for non-books.
 * @param {string} seed     Color hash seed; defaults to title. Pass the same
 *                          seed on every surface so the book keeps its color.
 * @param {string} variant  'full' (cards) · 'large' (player sheet) ·
 *                          'compact' (small thumbs, title only) ·
 *                          'mini' (tiny tiles, initial only).
 */
const AutoBookCover = ({ title, author = '', typeLabel = '', seed = '', variant = 'full', className = '' }) => {
  const background = useMemo(() => {
    const [from, to] = AUTO_COVER_GRADIENTS[hashString(seed || title) % AUTO_COVER_GRADIENTS.length];
    return `linear-gradient(150deg, ${from}, ${to})`;
  }, [seed, title]);

  const initial = String(title || 'B').trim().charAt(0).toUpperCase() || 'B';

  return (
    <div className={`${styles.cover} ${styles[variant] || ''} ${className}`} style={{ background }}>
      <span className={styles.spineEdge} aria-hidden="true" />
      <div className={styles.inner}>
        {variant === 'mini' ? (
          <span className={styles.initial}>{initial}</span>
        ) : (
          <>
            <span className={styles.rule} aria-hidden="true" />
            <span className={styles.title}>{title}</span>
            {author && (variant === 'full' || variant === 'large') && (
              <span className={styles.author}>{author}</span>
            )}
            <span className={styles.rule} aria-hidden="true" />
          </>
        )}
      </div>
      {typeLabel && variant === 'full' && <span className={styles.typeBadge}>{typeLabel}</span>}
    </div>
  );
};

export default AutoBookCover;
